/**
 * Pipeline JSON-LD unique : graphe global + nœuds page, sérialisation déterministe.
 * Une seule balise <script type="application/ld+json"> par document après fusion.
 */

import { companyInfo } from '../config/company';

const SITE = 'https://rakb.ma';

const sameAsProfiles: string[] = [
  companyInfo.social.instagram,
  companyInfo.social.facebook,
  companyInfo.social.linkedin,
  companyInfo.social.twitter,
].filter((u): u is string => Boolean(u && u.startsWith('http')));

export interface RakbMergedJsonLd {
  '@context': 'https://schema.org';
  '@graph': Record<string, unknown>[];
}

/** @id stable du service « marketplace » (pas une agence physique locale). */
export const RAKB_MARKETPLACE_SERVICE_ID = `${SITE}/#marketplace-mobility-service` as const;

/** Entités site : Organization, WebSite (+ SearchAction), Service marketplace — @id stables */
export const buildGlobalGraph = (): RakbMergedJsonLd => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${SITE}/#organization`,
      name: 'RAKB',
      alternateName: ['RAKB.ma', 'RAKB Maroc'],
      description:
        "Plateforme de location de voitures au Maroc : comparez les offres d'agences partenaires et réservez en ligne pour Casablanca, Marrakech, Rabat, Tanger et tout le royaume.",
      url: SITE,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE}/og-image.png`,
      },
      email: companyInfo.email,
      telephone: companyInfo.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: companyInfo.address.street,
        addressLocality: companyInfo.address.city,
        postalCode: companyInfo.address.postalCode,
        addressCountry: 'MA',
      },
      areaServed: {
        '@type': 'Country',
        name: 'Morocco',
      },
      sameAs: sameAsProfiles,
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE}/#website`,
      name: 'RAKB — Location voiture Maroc',
      url: SITE,
      inLanguage: ['fr-FR', 'en-US', 'ar-MA'],
      publisher: { '@id': `${SITE}/#organization` },
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${SITE}/search?location={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'Service',
      '@id': RAKB_MARKETPLACE_SERVICE_ID,
      name: 'RAKB — Marketplace de location de voitures au Maroc',
      url: SITE,
      serviceType: 'Car rental',
      description:
        'Plateforme de réservation en ligne : mise en relation avec des agences de location partenaires au Maroc pour tourisme et affaires.',
      provider: { '@id': `${SITE}/#organization` },
      areaServed: { '@type': 'Country', name: 'Morocco' },
      availableChannel: {
        '@type': 'ServiceChannel',
        serviceUrl: SITE,
      },
    },
  ],
});

export interface PageJsonLdInput {
  canonicalUrl: string;
  pageName: string;
  description: string;
  breadcrumb: { name: string; url: string }[];
  /** Lien WebPage → entité principale (ex. Product véhicule). */
  mainEntity?: { '@id': string };
}

/** WebPage + BreadcrumbList référencés par @id (pas de second Organization) */
export const buildPageGraph = (input: PageJsonLdInput): Record<string, unknown>[] => {
  const wpId = `${input.canonicalUrl}#webpage`;
  const bcId = `${input.canonicalUrl}#breadcrumb`;
  return [
    {
      '@type': 'WebPage',
      '@id': wpId,
      url: input.canonicalUrl,
      name: input.pageName,
      description: input.description,
      isPartOf: { '@id': `${SITE}/#website` },
      about: { '@id': RAKB_MARKETPLACE_SERVICE_ID },
      breadcrumb: { '@id': bcId },
      ...(input.mainEntity ? { mainEntity: input.mainEntity } : {}),
    },
    {
      '@type': 'BreadcrumbList',
      '@id': bcId,
      itemListElement: input.breadcrumb.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.name,
        item: b.url,
      })),
    },
  ];
};

export interface FaqJsonLdItem {
  question: string;
  answer: string;
}

export const buildFaqPageGraph = (canonicalUrl: string, faqs: FaqJsonLdItem[]): Record<string, unknown> => ({
  '@type': 'FAQPage',
  '@id': `${canonicalUrl}#faq`,
  mainEntity: faqs.map(f => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.answer,
    },
  })),
});

/** ItemList aligné sur les liens visibles (extrait du nav prerender). */
export const buildRelatedDestinationsItemList = (
  canonicalUrl: string,
  items: { name: string; url: string }[],
  listName: string
): Record<string, unknown> => ({
  '@type': 'ItemList',
  '@id': `${canonicalUrl}#related-destinations`,
  name: listName,
  numberOfItems: items.length,
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: item.url,
  })),
});

export const mergeGlobalAndPageNodes = (pageNodes: Record<string, unknown>[]): RakbMergedJsonLd => {
  const g = buildGlobalGraph();
  return {
    '@context': 'https://schema.org',
    '@graph': [...g['@graph'], ...pageNodes],
  };
};

export const serializeJsonLd = (doc: RakbMergedJsonLd): string => JSON.stringify(doc);
